/**
 * Application entry point.
 *
 * Import order matters. `./config/icons.js` calls `setIconPath()` and must run
 * before any Web Awesome component is evaluated, so it is imported first. See
 * that file for why.
 */

import "./config/icons.js";
import "./app/components.js";
import "@web.awesome.me/webawesome-pro/dist/styles/webawesome.css";
import "./styles/kincode-theme.css";
import "./styles/app.css";

import { loadConfig } from "./config/environment.js";
import { startRouter } from "./app/router.js";
import { installErrorBoundary } from "./app/error-boundary.js";

// Install the last-resort error boundary before anything else can throw, so a
// failure in configuration resolution or router start-up shows a plain recovery
// panel rather than a blank page. It logs nothing off-device — see the module.
installErrorBoundary();

/**
 * Note on the Font Awesome kit code: it is intentionally NOT set here. The kit
 * code authenticates requests for premium CDN assets, and KinCode self-hosts its
 * icons instead (see config/icons.js), so the browser never needs it. Keeping it
 * out of the bundle keeps a Pro credential off the client entirely.
 */

/*
 * `VITE_FORCE_MOCKS` is read as its own expression rather than relied upon to
 * appear inside `import.meta.env`. Vite replaces a bare `import.meta.env` with an
 * object literal built from the .env files, and a value injected through
 * `define` is not part of that object — so spreading it in explicitly is what
 * makes the e2e build's mock pinning actually take effect. Verified by building
 * both modes and checking behaviour, not by reading the config.
 */
const config = loadConfig(
  /** @type {Record<string, string | undefined>} */ (
    /** @type {unknown} */ ({
      ...import.meta.env,
      VITE_FORCE_MOCKS: import.meta.env.VITE_FORCE_MOCKS,
      // True only under `vite dev`. Used to reject a built bundle that would point
      // at the emulators — see loadConfig. Cast because `import.meta.env` also
      // carries Vite's own booleans (PROD, SSR), which loadConfig ignores.
      DEV: import.meta.env.DEV ? "true" : "false",
    })
  ),
);

const root = document.getElementById("app");

if (root) {
  startRouter(root, config);
}
